import { Text, color, font } from "@smoothincorg/smooth-ui"
import dayjs from "dayjs"
import { produce } from "immer"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FlexBox } from "src/components/Box"
import { BorderedButton } from "src/components/Button"
import { Checkbox } from "src/components/Checkbox"
import { PokeLoading } from "src/components/Loading"
import { MemoType } from "src/components/Memo"
import { Card } from "src/components/page-directory/Card"
import { Wrapper } from "src/components/page-directory/Wrapper"
import { Smoother, useCurrentSmoother } from "src/features/auth"
import { MainExaminationScore as MainExaminationScoreType } from "src/hooks/useMainExaminationScoresSWR"
import { MovingApplication } from "src/hooks/useMovingApplicationsSWR/types"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SnakeCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants/value"
import { useAppState, useSetAppState } from "src/store/AppStateContext"
import styled from "styled-components"
import useSWR from "swr/immutable"
import { EmergencyMessages } from "./EmergencyMessages"
import * as styles from "./ForCIC.css"
import { ForCICNav } from "./ForCICNav"
import { MainExaminationScore } from "./MainExaminationScore"
import { MainExaminationScoreWrapper } from "./MainExaminationScoreWrapper"

export type Proposal = {
  id: number
  user_id: number
  store_id: number
  introduction_status: number
  search_status: number
  application_status: number
  memo: number
  version: "1" | "2"
  enabled: true
  created_at: string
  updated_at: string
  gardia_transaction_code: null
  propose_frequency: number
}

type SearchHouse = {
  id: number
  proposal_id: number
  rent_lower: number
  rent_upper: number
  rent_include: string[]
  moving_target_on: string
  tab: null
  prefecture: string
  breadth_lower: number
  breadth_upper: number
  build_const: string[]
  build_type: string[]
  building_age: string
  madori: string[]
  ambiguous: string
  route_line: string[]
  walk: string
  stations: string[]
  target_cities: string[]
  target_pref: string | null
  requirement: string
  special: string[]
  house_urls: string[]
  enabled: boolean
  created_at: string
  updated_at: string
  moving_purpose: string[]
}

type SimplifiedExamination = {
  annual_interest_rate: 0.14
  bonus_yearly: 0
  child_count: 2
  company_address: null
  company_antisocial_examination: null
  company_bankruptcy_examination: null
  company_fact_examination: null
  company_name: "株式会社ひっこしやさん"
  company_name_kana: "カブシキカイシャスムーズ"
  company_postal_code: null
  company_telephone_number: "000-0000-0000"
  created_at: "2022-02-23T15:56:15.000+09:00"
  credit_line: 100
  current_house_rent: 10
  current_residence_status: "賃貸"
  enabled: true
  family_type: "独身" | "既婚"
  gardia_result: 1
  had_arrears: false
  has_credit: true
  has_loan: true
  telephone_number: "000-0000-0000"
  postcode: "000-0000"
  house_mates: [
    {
      house_mate_name: "花澤"
      house_mate_relationship: "友人"
      house_mate_income_yearly: "5"
    }
  ]
  household: number
  id: 7
  income_monthly: 40
  income_yearly: 480
  job: "会社員"
  job_description: "フリーランスのアパレル業界でのコンサルティング業"
  job_term: 5
  loan_monthly: 6
  loan_purpose: null
  loan_purpose_other: null
  loan_total: 140
  number_of_payment: 12
  payday: 24
  proposal_id: 7
  rent_subsidy: null
  updated_at: "2022-02-23T15:56:15.000+09:00"
  user_antisocial_examination: null
  user_bankruptcy_examination: null
  user_id: 6
  birthday: string
  first_furigana: string
  first_name: string
  last_furigana: string
  last_name: string
}

export type User = {
  id: number
  status: number
  first_name: string
  last_name: string
  first_furigana: string
  last_furigana: string
  line_user_name: string
  address: string
  icon_src: string
  gender: "男性" | "女性"
  birthday: string
  enabled: true
  created_at: string
  updated_at: string
  tracking_id: string
  line_profile_synced_at: string
  is_no_bypass: boolean
  telephone_number: string
  postal_code: string
  birthday_ja: string
  proposals_documents: string
  around_initial_cost: number | "対象物件がありませんでした"
  around_initial_cost_status: string
  around_initial_cost_url: string
  latest_simplified_examination: SimplifiedExamination
  memos: MemoType[]
  latest_address?: {
    postcode?: string
  }
  interest_add_on_when_examination: number
}

const MAIN_EXAMINATION_SCORE_TYPE_KEY = "main_examination_scores_type"
const MAIN_EXAMINATION_SCORE_TYPES = {
  未完了: "undone",
  "未完了(いますぐ)": "now",
  "未完了(1ヶ月)": "one_month",
  "未完了(2ヶ月)": "two_month",
  "未完了(3ヶ月)": "three_month",
  "未完了(未定 + その他)": "other",
  "要確認(90日以内)": "pending",
  "通過(90日以内)": "true",
  "部分(90日以内)": "part",
} as const

export const SEARCH_HOUSE_STATUS_TYPE_KEY = "search_house_status"
export const SEARCH_HOUSE_STATUS_TYPES = {
  全件: "search_house_status_all",
  今すぐ: "search_house_status_now",
  "1か月以内": "search_house_status_one_month",
  "2か月以内": "search_house_status_two_month",
  "3か月以内": "search_house_status_three_month",
  "その他＋未定": "search_house_status_other",
}

export const EXAM_STATUS_TYPE_KEY = "exam_status"
export const EXAM_STATUS_TYPES = {
  新規: "exam_status_new",
  "アサイン済(自分)": "exam_status_assigned_to_me",
  "アサイン済(他人)": "exam_status_assigned_to_others",
  要確認: "exam_status_check_required",
  全額通過: "exam_status_pass",
  部分通過: "exam_status_partial",
  否決: "exam_status_reject",
} as const

export const AI_EXAM_STATUS_TYPE_KEY = "ai_exam_status"
export const AI_EXAM_STATUS_TYPES = {
  全件: "ai_exam_status_all",
  API対象外: "ai_exam_status_not_target",
  可決: "ai_exam_status_pass",
  "否決:": "ai_exam_status_reject",
} as const

export const INTRODUCTION_STATUS_TYPE_KEY = "introduction_status"
export const INTRODUCTION_STATUS_TYPES = {
  全件: "intro_status_all",
  店舗利用: "intro_status_receiving",
  集客支援: "intro_status_sending",
} as const

export const RelevantQueryParams = [
  EXAM_STATUS_TYPE_KEY,
  SEARCH_HOUSE_STATUS_TYPE_KEY,
  AI_EXAM_STATUS_TYPE_KEY,
  MAIN_EXAMINATION_SCORE_TYPE_KEY,
  INTRODUCTION_STATUS_TYPE_KEY,
]

type ContainerProps = {
  mainExaminationScoresType: (typeof MAIN_EXAMINATION_SCORE_TYPES)[keyof typeof MAIN_EXAMINATION_SCORE_TYPES]
}

export type ForCICProps = {
  isSimpleMode: boolean
  isLegacyLoad: boolean
  className?: string
  mainExaminationScores: MainExaminationScoreType[]
  newly: MainExaminationScoreType[]
  movingApplications: SnakeCase<MovingApplication>[]
  proposals: Proposal[]
  users: User[]
  searchHouses: SearchHouse[]
  checkedUsers: number[]
  handleCheckUser: React.ChangeEventHandler<HTMLInputElement>
  handleCheckUserAll: () => void
  currentSmoother: Smoother
  isValidating: boolean
  isFullLoading: boolean
  validationMessages: string[]
  emergencyMessages: string[]
  handleCsvFormPost: () => void
  anchorRef: React.MutableRefObject<HTMLAnchorElement>
  handleSeeNewlyClick: () => void
  displayedUsers: User[]
  mutateForCIC: (targetId: number) => void
}

export const IsNotAgreed = (user: User) => {
  const isV3Agreed = (user.status & 8192) === 0
  const is2023DecAgreed = (user.status & 16384) === 0

  return isV3Agreed && is2023DecAgreed
}

const MainExaminationScoreComponent = (mes: MainExaminationScoreType, props: ForCICProps) => {
  if (props.isLegacyLoad) {
    return MainExaminationScoreComponentOld(mes, props)
  }

  return <MainExaminationScoreWrapper mes={mes} props={props} />
}

const MainExaminationScoreComponentOld = (mes: MainExaminationScoreType, props: ForCICProps) => {
  const movingApplication = props.movingApplications?.find(
    (ma) => Number(ma.id) === Number(mes?.moving_application_id)
  )
  const proposal = props.proposals?.find(
    (p) => Number(p.id) === Number(movingApplication?.proposal_id)
  )
  const user = props.users?.find((u) => Number(u.id) === Number(proposal?.user_id))

  if (!movingApplication) return <Text>.</Text>
  if (!proposal) return <Text>.</Text>
  if (!user) return <Text>.</Text>

  return (
    <MainExaminationScore
      key={mes.id}
      user={user}
      mes={mes}
      proposal={proposal}
      {...props}
      className={null}
    />
  )
}

const onCheckedCheckUserAll = (checkedUsers: number[], displayedUsers: User[]): boolean => {
  if (!displayedUsers) return false

  return displayedUsers?.every((user) => checkedUsers.includes(user.id))
}

// eslint-disable-next-line react/display-name
const Component = React.memo<ForCICProps>((props) => {
  return (
    <div className={props.className}>
      <Wrapper className="main-examinarion-scores" maxWidth={1200}>
        <Card className={`${props.className}__nav`}>
          <FlexBox justifyContent="space-between" className="default-action" flexWrap="wrap">
            <ForCICNav />
            <FlexBox justifyContent="flex-end">
              <Checkbox
                checked={onCheckedCheckUserAll(props.checkedUsers, props.displayedUsers)}
                onChange={props.handleCheckUserAll}
                text="全件チェックする"
              />
              <BorderedButton
                onClick={() => {
                  if (
                    props.validationMessages.length > 0 &&
                    !confirm("エラーがありますがダウンロードします？")
                  )
                    return
                  props.handleCsvFormPost()
                }}
              >
                照会データをダウンロードする
              </BorderedButton>
            </FlexBox>
            <a href="" download ref={props.anchorRef}></a>
          </FlexBox>
        </Card>
        <FlexBox
          className={`${props.className}__validations`}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          {props.emergencyMessages.length
            ? props.emergencyMessages.map((message) => (
                <Text size={14} weight="bold" color={"red"} key={message}>
                  {message}
                </Text>
              ))
            : null}
          {props.validationMessages.map((message) => (
            <Text size={14} color={"red"} key={message}>
              {message}
            </Text>
          ))}
        </FlexBox>

        {props.newly?.length ? (
          <div className="">
            <button className={styles.newlyButton} onClick={props.handleSeeNewlyClick}>
              {props.newly?.length}件の新着審査対象ユーザー
            </button>
          </div>
        ) : null}

        {props.isFullLoading ? (
          <PokeLoading />
        ) : props.mainExaminationScores?.length ? (
          [...props.mainExaminationScores]
            ?.sort((a, b) => {
              const getCreatedAt = (mes: MainExaminationScoreType) => {
                return dayjs(mes.created_at)
              }

              return getCreatedAt(a).isAfter(getCreatedAt(b)) ? 1 : -1
            })
            .map((mes) => {
              return MainExaminationScoreComponent(mes, props)
            })
        ) : props.isValidating ? (
          <PokeLoading />
        ) : !props.newly?.length ? (
          <Text size={14}>新規審査対象ユーザーは居ません</Text>
        ) : null}
      </Wrapper>
    </div>
  )
})

const StyledComponent = styled(Component)`
  padding: 30px 0;
  background-color: ${color.lightGray};
  min-height: calc(100vh - 56px);

  &__nav {
    margin-bottom: 12px;

    .checkbox {
      margin-right: 12px;
      padding: 0 8px;
    }
  }

  .default-action {
    > * {
      margin: 10px;
    }
  }

  &__validations {
    > .smooth-ui-Text {
      margin-bottom: 12px;
    }
  }

  .main-examination-score {
    background-color: #ffffff;
    margin-bottom: 18px;

    &__row {
      padding: 20px 20px;

      > .smooth-ui-Title {
        width: 140px;
      }

      > .checkbox {
        height: 44px;
        width: 200px;
      }

      > .smooth-ui-FlexBox {
        > * {
          margin-right: 20px;
        }

        > label {
          width: 300px;
          margin-left: auto;
        }

        > .smooth-ui-FlexBox {
          label {
            width: 120px;
            margin: 0 18px;
          }
        }
      }
    }

    &__actions {
      > * {
        ${font(14)};
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 5px 10px;
        border-radius: 8px;
        margin-right: 4px;
      }
    }

    .memos {
      width: 300px;

      > * {
        margin-bottom: 8px;
      }
    }

    .memo-form {
      width: 500px;
      > * {
        margin-bottom: 8px;
      }
      .smooth-ui-GrayRoundTextArea {
        height: 60px;
      }
    }
  }
`

export type ForCICMainExaminationScores = {
  main_examination_scores: MainExaminationScoreType[]
  moving_applications: SnakeCase<MovingApplication>[]
  proposals: Proposal[]
  users: User[]
  search_houses: SearchHouse[]
}

const Container: React.VFC<ContainerProps> = (props) => {
  const [swrKey, setSwrKey] = useState("key")
  const [refreshCount, setRefreshCount] = useState(0)
  const [isFullLoading, setIsFullLoading] = useState(false)

  const router = useRouter()
  const { isReady } = router

  const [_hasSetDefaultExamStatus, setHasSetDefaultExamStatus] = useState(false)

  useEffect(() => {
    if (isReady) {
      if (!router.query[EXAM_STATUS_TYPE_KEY]) {
        // デフォルトのexam_statusを設定
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              [EXAM_STATUS_TYPE_KEY]: "exam_status_new",
            },
          },
          undefined,
          { shallow: true }
        )
        setHasSetDefaultExamStatus(true) // avoid inifiinite loop
      } else {
        setSwrKey((current) => current + 1)
      }
    }
  }, [isReady, router.query])

  const examStatus = router.query[EXAM_STATUS_TYPE_KEY]
  const isSimpleMode = isReady && examStatus === "exam_status_new"
  const isLegacyLoad = isReady && router.query.load === "legacy"

  const mainExaminationsScoreUrl = useMemo(() => {
    if (!isReady) return null

    const queryParams: any = {}

    for (const key of RelevantQueryParams) {
      if (router.query[key]) {
        queryParams[key] = router.query[key]
      }
    }

    queryParams[EXAM_STATUS_TYPE_KEY] = examStatus

    const queryString = new URLSearchParams(queryParams).toString()
    const actionName = isLegacyLoad ? "for_cic_all" : "for_cic_simple"

    let url = null
    if (queryString) {
      url = `${SMOOTH_SMOOTHER_API_URL}/${actionName}?${queryString}`
    } else {
      url = `${SMOOTH_SMOOTHER_API_URL}/${actionName}`
    }

    return url
  }, [isReady, router.query])

  const { getWithToken, postWithToken } = useSmoothApi()
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const appState = useAppState()
  const setAppState = useSetAppState()

  const getUserByMes = useCallback(
    (mes: MainExaminationScoreType) => {
      const data = appState.forCIC.mainExaminationScores
      const movingApplication = data.moving_applications?.find(
        (ma) => Number(ma.id) === Number(mes?.moving_application_id)
      )
      const proposal = data.proposals?.find(
        (p) => Number(p.id) === Number(movingApplication?.proposal_id)
      )

      const user = data.users?.find((u) => Number(u.id) === Number(proposal?.user_id))

      return user
    },
    [appState.forCIC.mainExaminationScores]
  )

  const queryParamsString = JSON.stringify(router.query)
  const prevQueryParamsStringRef = useRef(queryParamsString)

  const mainExaminationScoresSWR = useSWR(
    swrKey,
    async (_key) => {
      if (!mainExaminationsScoreUrl) return null

      const isQueryParamsChanged = queryParamsString !== prevQueryParamsStringRef.current
      prevQueryParamsStringRef.current = queryParamsString

      if (isQueryParamsChanged) setIsFullLoading(true)
      const res = await getWithToken(mainExaminationsScoreUrl)
      const data = res.data as { data: ForCICMainExaminationScores }

      return data.data
    },
    {
      refreshInterval: 10000,
      revalidateIfStale: true,
      onSuccess: (data) => {
        setRefreshCount((current) => current + 1)

        setAppState((current) => {
          const newly = data?.main_examination_scores.filter(
            (self) =>
              !current.forCIC.mainExaminationScores?.main_examination_scores?.some(
                (d) => d.id === self.id
              )
          )

          const notNewly = data?.main_examination_scores.filter((self) =>
            current.forCIC.mainExaminationScores?.main_examination_scores?.some(
              (d) => d.id === self.id
            )
          )

          return produce(current, (draft) => {
            const isInitialFetch =
              isFullLoading || !draft.forCIC?.mainExaminationScores?.main_examination_scores?.length

            if (draft.forCIC.mainExaminationScores) {
              if (isInitialFetch) {
                draft.forCIC.mainExaminationScores.main_examination_scores = newly
              }

              if (!isInitialFetch) {
                draft.forCIC.mainExaminationScores.newly = newly
                draft.forCIC.mainExaminationScores.main_examination_scores = notNewly
              }

              draft.forCIC.mainExaminationScores.moving_applications = data.moving_applications

              draft.forCIC.mainExaminationScores.proposals = data.proposals

              draft.forCIC.mainExaminationScores.search_houses = data.search_houses

              draft.forCIC.mainExaminationScores.users = data.users
            }

            setIsFullLoading(false)
          })
        })
      },
    }
  )

  const { smoother } = useCurrentSmoother()

  const [checkedUsers, setCheckedUsers] = useState<number[]>([])

  const displayedUsers = appState.forCIC.mainExaminationScores?.users?.filter((user) => {
    const newlyMesUsers = appState.forCIC?.mainExaminationScores?.newly?.map((mes) =>
      getUserByMes(mes)
    )

    return !newlyMesUsers?.some((d) => d.id === user.id)
  })

  const handleCheckUser: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCheckedUsers((current) => {
      if (current.includes(Number(e.target.value))) {
        return current.filter((c) => c !== Number(e.target.value))
      }
      return [...current, Number(e.target.value)]
    })
  }

  const handleCheckUserAll = () => {
    setCheckedUsers((current) => {
      if (displayedUsers?.every((user) => current.includes(user.id))) return []
      return displayedUsers?.map((user) => user.id)
    })
  }

  const users = appState.forCIC.mainExaminationScores?.users

  const [emergencyMessagesData, setEmergencyMessagesData] = useState<string[] | null>(null)
  useEffect(() => {
    if (isSimpleMode) {
      getWithToken(`${SMOOTH_SMOOTHER_API_URL}/for_cic/emergency_messages`).then((res) => {
        setEmergencyMessagesData(res.data?.messages)
      })
    }
  }, [refreshCount])

  const emergencyMessages = useMemo(() => {
    if (isSimpleMode && emergencyMessagesData != null) {
      return emergencyMessagesData
    }

    return EmergencyMessages(
      displayedUsers,
      mainExaminationScoresSWR?.data?.proposals,
      mainExaminationScoresSWR?.data?.search_houses
    )
  }, [
    router.isReady,
    emergencyMessagesData,
    displayedUsers,
    mainExaminationScoresSWR?.data?.proposals,
    mainExaminationScoresSWR?.data?.search_houses,
  ])

  const validationMessages = useMemo(() => {
    if (checkedUsers == null) {
      window.alert("全件詳細読み込みを行ってから選択してください")
      return []
    }

    const messages: string[] = []

    for (const checkedUsersId of checkedUsers) {
      const user = users?.find((user) => user.id === checkedUsersId)

      if (user) {
        if (!user.last_furigana || !user.first_furigana) {
          messages.push(`ID:${user.id}のカナ氏名が抜けてます！`)
        }

        if (!user.birthday) {
          messages.push(`ID:${user.id}の生年月日が抜けてます！`)
        }

        if (dayjs(user.birthday).format("YYYY-MM-DD") === "9999-12-31") {
          messages.push(`ID:${user.id}の生年月日が初期値のまま！`)
        }

        if (!user.telephone_number) {
          messages.push(`ID:${user.id}の電話番号が抜けてます！`)
        }

        if (user.around_initial_cost === "対象物件がありませんでした") {
          messages.push(`ID:${user.id}の申込額が算出できません！`)
        }

        if (!user?.postal_code || user.postal_code === "郵便番号未設定") {
          messages.push(`ID:${user.id}の郵便番号が取得できません！`)
        }

        if (IsNotAgreed(user)) {
          messages.push(`ID:${user.id}の規約同意が取れていないです！！！`)
        }
      }
    }

    return messages
  }, [checkedUsers, users])

  const handleCsvFormPost = async () => {
    const formData = new FormData()

    const users = appState.forCIC.mainExaminationScores?.main_examination_scores
      .filter((mes) => {
        const user = getUserByMes(mes)
        return checkedUsers.includes(Number(user.id))
      })
      .sort((a, b) => {
        const getCreatedAt = (mes: MainExaminationScoreType) => {
          return dayjs(mes.created_at)
        }

        return getCreatedAt(a).isAfter(getCreatedAt(b)) ? 1 : -1
      })
      .map((mes) => {
        const user = getUserByMes(mes)
        return user.id
      })

    for (const userId of users) {
      formData.append("user_ids[]", String(userId))
    }
    const res = await postWithToken(
      `${SMOOTH_SMOOTHER_API_URL}/for_cic/generate_cic_csv_for_inquiry`,
      formData,
      { responseType: "arraybuffer" }
    )

    const blob = new Blob([res.data], { type: "text/csv" })
    const url = URL || webkitURL
    anchorRef.current.href = url.createObjectURL(blob)
    anchorRef.current.download = `${dayjs().format("YYY-MM-DD_HHmm")}.csv`
    anchorRef.current.click()
  }

  const handleSeeNewlyClick = () => {
    setAppState((current) =>
      produce(current, (draft) => {
        if (draft.forCIC.mainExaminationScores.main_examination_scores) {
          draft.forCIC.mainExaminationScores.main_examination_scores = [
            ...draft.forCIC.mainExaminationScores.main_examination_scores,
            ...draft.forCIC.mainExaminationScores.newly,
          ]

          draft.forCIC.mainExaminationScores.newly = []
        }
      })
    )
  }

  const mutateForCIC = (targetId: number) => {
    mainExaminationScoresSWR.mutate(null, {
      optimisticData: (data) => {
        return {
          ...data,
          main_examination_scores: data?.main_examination_scores.filter(
            (mes) => mes.id !== targetId
          ),
        }
      },
      rollbackOnError: true,
    })
  }

  return (
    <StyledComponent
      isSimpleMode={isSimpleMode}
      isLegacyLoad={isLegacyLoad}
      isValidating={mainExaminationScoresSWR.isValidating}
      isFullLoading={isFullLoading}
      mainExaminationScores={appState.forCIC.mainExaminationScores?.main_examination_scores}
      movingApplications={appState.forCIC.mainExaminationScores?.moving_applications}
      proposals={appState.forCIC.mainExaminationScores?.proposals}
      searchHouses={appState.forCIC.mainExaminationScores?.search_houses}
      newly={appState.forCIC?.mainExaminationScores?.newly}
      users={users}
      displayedUsers={displayedUsers}
      checkedUsers={checkedUsers}
      emergencyMessages={emergencyMessages}
      handleCheckUser={handleCheckUser}
      handleCheckUserAll={handleCheckUserAll}
      currentSmoother={smoother}
      validationMessages={validationMessages}
      handleCsvFormPost={handleCsvFormPost}
      anchorRef={anchorRef}
      handleSeeNewlyClick={handleSeeNewlyClick}
      mutateForCIC={mutateForCIC}
    />
  )
}

export const ForCIC = Container
